import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import notificationService from '../../../services/notificationService';

const OptimalTimingSuggestions = ({ suggestions, onSelectTime }) => {
  const [selectedSuggestion, setSelectedSuggestion] = React.useState(null);

  // Trigger intelligent notification when new optimal suggestions are available
  React.useEffect(() => {
    if (suggestions?.length > 0) {
      const availabilityData = {
        newSlots: suggestions?.map(s => `${s?.day} ${s?.time}`)?.slice(0, 3),
        userAvailability: ['flexible'],
      };

      const scheduleHistory = {
        typicalAvailability: ['evenings', 'weekends'],
        successfulTimes: suggestions?.filter(s => s?.compatibility > 80)?.map(s => `${s?.day} ${s?.time}`),
      };

      const relationshipContext = {
        compatibilityScore: Math.max(...suggestions?.map(s => s?.compatibility)),
        moodAlignment: 'high',
      };

      // Send intelligent notification about new optimal times
      notificationService?.notifyIntelligentAvailability(
        availabilityData,
        scheduleHistory,
        relationshipContext
      );
    }
  }, [suggestions]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (time) => {
    const [hours, minutes] = time?.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 75) return 'text-primary';
    return 'text-warning';
  };

  const getScoreBg = (score) => {
    if (score >= 90) return 'bg-success/10';
    if (score >= 75) return 'bg-primary/10';
    return 'bg-warning/10';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6">
      <div className="flex items-center space-x-2 mb-4 md:mb-5">
        <Icon name="Sparkles" size={20} color="var(--color-accent)" />
        <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
          AI-Powered Optimal Times
        </h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4 md:mb-6">
        Based on both partners' mood patterns, availability, and historical data
      </p>
      <div className="space-y-3 md:space-y-4">
        {suggestions?.map((suggestion, index) => (
          <div
            key={suggestion?.id}
            className="bg-background rounded-lg border border-border p-4 hover:shadow-therapeutic transition-therapeutic"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-base md:text-lg font-heading font-semibold text-foreground">
                    {formatDate(suggestion?.date)}
                  </span>
                  {index === 0 && (
                    <span className="px-2 py-0.5 bg-accent/10 text-accent text-xs font-medium rounded">
                      Best Match
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {formatTime(suggestion?.time)} • {suggestion?.duration} minutes
                </p>
              </div>
              
              <div className={`flex items-center space-x-1 px-3 py-1.5 rounded-md ${getScoreBg(suggestion?.score)}`}>
                <Icon 
                  name="TrendingUp" 
                  size={14} 
                  color={`var(--color-${suggestion?.score >= 90 ? 'success' : suggestion?.score >= 75 ? 'primary' : 'warning'})`}
                />
                <span className={`text-sm font-medium ${getScoreColor(suggestion?.score)}`}>
                  {suggestion?.score}%
                </span>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-start space-x-2">
                <Icon name="Heart" size={16} color="var(--color-muted-foreground)" className="mt-0.5 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Mood Match:</span> {suggestion?.moodMatch}
                </p>
              </div>
              
              <div className="flex items-start space-x-2">
                <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" className="mt-0.5 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Availability:</span> {suggestion?.availability}
                </p>
              </div>

              <div className="flex items-start space-x-2">
                <Icon name="Activity" size={16} color="var(--color-muted-foreground)" className="mt-0.5 flex-shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Energy Levels:</span> {suggestion?.energyLevels}
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              iconName="Plus"
              iconPosition="left"
              onClick={() => onSelectTime(suggestion)}
              fullWidth
            >
              Schedule This Time
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OptimalTimingSuggestions;